import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, HelpCircle, MessageCircle, Sparkles } from "lucide-react";

const faqData = [
    {
        category: "General",
        items: [
            {
                question: "How quickly can we expect to see results?",
                answer:
                    "While \"momentum\" builds over time, we typically see initial data signals and performance lifts within the first 30 to 60 days as our precision targeting begins to optimize.",
            },
            {
                question: "Will I have transparency into where my ad spend is going?",
                answer:
                    "Absolutely. We provide real-time dashboards and scheduled deep-dives so you can see exactly how every dollar is contributing to your brand’s momentum.",
            },
            {
                question: "What metrics do you prioritize in your reports?",
                answer:
                    "While we track everything, we focus on the \"North Star\" metrics: Return on Ad Spend (ROAS), Customer Acquisition Cost (CAC), and overall Lifetime Value (LTV) growth.",
            },
            {
                question: "How does your \"precision targeting\" differ from standard platform targeting?",
                answer:
                    "We don't just \"set and forget.\" We leverage advanced analytics and custom audience modeling to find the overlap between your best customers and untapped market segments.",
            },
        ],
    },
];

const FAQs = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [activeCategory, setActiveCategory] = useState("all");

    const toggleFAQ = (id) => {
        setActiveIndex(activeIndex === id ? null : id);
    };

    const filteredFaqs =
        activeCategory === "all"
            ? faqData
            : faqData.filter((group) => group.category === activeCategory);

    return (
        <div className="min-h-screen bg-white relative overflow-hidden">

            {/* ─── Hero Section ─── */}
            <div className="relative bg-gradient-to-br from-[#00809D] via-[#006d87] to-[#005568] overflow-hidden">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/5 rounded-full translate-y-1/2 -translate-x-1/3" />
                <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-white/5 rounded-2xl rotate-45" />

                <div className="relative z-10 pt-16 sm:pt-20 lg:pt-24 pb-20 sm:pb-24 lg:pb-28 px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 25 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md px-5 py-2 rounded-full mb-6 border border-white/20">
                            <HelpCircle className="w-4 h-4 text-white/90" />
                            <span className="text-white/90 font-medium text-sm">Help Center</span>
                        </div>

                        <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-5 leading-tight tracking-tight">
                            Frequently Asked{" "}
                            <span className="block sm:inline">Questions</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
                            Find answers to common questions about our services, pricing, and how we can help grow your business.
                        </p>
                    </motion.div>
                </div>

                {/* Curved bottom */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                        <path d="M0 80V40C240 0 480 0 720 20C960 40 1200 60 1440 40V80H0Z" fill="white" />
                    </svg>
                </div>
            </div>

            {/* ─── Category Filter ─── */}
            <div className="px-4 sm:px-6 lg:px-8 -mt-4 relative z-20">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="flex flex-wrap justify-center gap-2 sm:gap-3"
                    >
                        {["all", ...faqData.map((g) => g.category)].map((cat) => (
                            <button
                                key={cat}
                                onClick={() => { setActiveCategory(cat); setActiveIndex(null); }}
                                className={`px-4 sm:px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${activeCategory === cat
                                    ? "bg-[#00809D] text-white shadow-lg shadow-[#00809D]/25"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                                    }`}
                            >
                                {cat === "all" ? "All Questions" : cat}
                            </button>
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* ─── FAQ Content ─── */}
            <div className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
                <div className="max-w-4xl mx-auto space-y-10 sm:space-y-12">
                    {filteredFaqs.map((group, groupIndex) => (
                        <motion.div
                            key={group.category}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: groupIndex * 0.1 }}
                        >
                            {/* Category Header */}
                            <div className="flex items-center gap-3 mb-5 sm:mb-6">
                                <div className="w-9 h-9 rounded-xl bg-[#00809D]/10 flex items-center justify-center">
                                    <Sparkles className="w-4 h-4 text-[#00809D]" />
                                </div>
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                                    {group.category}
                                </h2>
                                <div className="flex-1 h-px bg-gray-100" />
                            </div>

                            {/* Questions */}
                            <div className="space-y-3 sm:space-y-4">
                                {group.items.map((faq, faqIndex) => {
                                    const id = `${groupIndex}-${faqIndex}`;
                                    const isOpen = activeIndex === id;

                                    return (
                                        <div
                                            key={id}
                                            className={`rounded-2xl border transition-all duration-300 overflow-hidden ${isOpen
                                                ? "bg-white border-[#00809D]/20 shadow-lg shadow-[#00809D]/8"
                                                : "bg-gray-50 border-gray-100 hover:border-gray-200 hover:shadow-sm"
                                                }`}
                                        >
                                            <button
                                                onClick={() => toggleFAQ(id)}
                                                className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 text-left"
                                            >
                                                <div className="flex items-start gap-3 sm:gap-4">
                                                    <div
                                                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-300 ${isOpen
                                                            ? "bg-[#00809D] text-white"
                                                            : "bg-[#00809D]/10 text-[#00809D]"
                                                            }`}
                                                    >
                                                        <MessageCircle className="w-4 h-4" />
                                                    </div>
                                                    <h3
                                                        className={`text-base sm:text-lg font-semibold transition-colors ${isOpen ? "text-[#00809D]" : "text-gray-800"
                                                            }`}
                                                    >
                                                        {faq.question}
                                                    </h3>
                                                </div>
                                                <ChevronDown
                                                    className={`w-5 h-5 flex-shrink-0 transition-all duration-300 ${isOpen
                                                        ? "rotate-180 text-[#00809D]"
                                                        : "text-gray-400"
                                                        }`}
                                                />
                                            </button>

                                            <AnimatePresence>
                                                {isOpen && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: "auto", opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className="px-5 sm:px-6 pb-5 sm:pb-6 pl-16 sm:pl-[72px]">
                                                            <div className="w-12 h-0.5 bg-[#00809D]/20 rounded-full mb-3" />
                                                            <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                                                                {faq.answer}
                                                            </p>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* ─── CTA Section ─── */}
            <div className="px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20 lg:pb-24">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="relative bg-gradient-to-br from-[#00809D] to-[#006d87] rounded-3xl p-8 sm:p-10 lg:p-12 text-center overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

                        <div className="relative">
                            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                                Still have questions?
                            </h3>
                            <p className="text-white/80 text-base sm:text-lg mb-7 max-w-lg mx-auto">
                                Can't find what you're looking for? Our team is here to help you with any questions.
                            </p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                                <a
                                    href="/contact"
                                    className="w-full sm:w-auto bg-white text-[#00809D] px-7 py-3.5 rounded-xl font-semibold text-sm hover:bg-gray-50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 shadow-lg shadow-black/10"
                                >
                                    Contact Us
                                </a>
                                <a
                                    href="/contact"
                                    className="w-full sm:w-auto bg-white/15 backdrop-blur-sm text-white px-7 py-3.5 rounded-xl font-semibold text-sm border border-white/20 hover:bg-white/25 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                                >
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default FAQs;
